import { create } from 'zustand';
import type { Asset, MediaType, ValueUpdater } from './seedanceTypes';
import { getLocalStorageValue, SEEDANCE_STORAGE_KEYS } from '../utils/seedanceStorage';
import { resolveValueUpdater } from './seedanceStoreUtils';

/**
 * 工作台以外的场景（例如「重新生成」弹窗）需要从素材库选图/视频/音频；
 * 与 libPickerTarget 同时设置：点击素材后触发 onApply 并清空 picker。
 */
export type LibPickerExternalConsumer = {
  nodeId: string;
  mediaType: MediaType;
  onApply: (asset: Asset) => void;
};

/** 线上虚拟人像素材组的轻量展示模型。 */
export interface LibraryGroup {
  id: string;
  name: string;
}

/**
 * Seedance 素材库公共状态。
 *
 * 素材页负责加载/上传/删除素材；创作页和右侧抽屉需要读取当前素材组、
 * 远程素材列表，并把素材应用到工作台节点，因此这些数据先提升为公共状态。
 *
 * 不要把素材库页面内部表单状态放进来，例如下拉菜单是否打开、新建素材组弹窗、
 * 删除确认弹窗、输入框内容等，它们应随素材库页面模块一起拆走。
 */
interface SeedanceLibraryState {
  /** 当前账号下的素材组列表。 */
  libAssetGroups: LibraryGroup[];
  /** 当前选中的素材组 id；初始值从 localStorage 恢复。 */
  libSelectedGroupId: string;
  /** 当前素材组下的远程素材列表。 */
  libRemoteAssets: Asset[];
  /** 素材列表分页：是否还有下一页 */
  libPortraitHasMore: boolean;
  /** 素材列表首屏加载中 */
  libPortraitLoading: boolean;
  /** 素材列表下拉加载更多中 */
  libPortraitLoadingMore: boolean;
  /** 素材列表按名称搜索（API query: name） */
  libPortraitSearchName: string;
  /** 侧边栏抽屉独立的搜索名称 */
  libDrawerSearchName: string;
  /** 素材选择器弹窗独立的搜索名称 */
  libPickerSearchName: string;

  /** 侧边栏抽屉独立的素材列表 */
  libDrawerRemoteAssets: Asset[];
  /** 侧边栏抽屉独立的分页状态 */
  libDrawerPortraitHasMore: boolean;
  libDrawerPortraitLoading: boolean;
  libDrawerPortraitLoadingMore: boolean;

  /** 素材选择器弹窗独立的素材列表 */
  libPickerRemoteAssets: Asset[];
  /** 素材选择器弹窗独立的分页状态 */
  libPickerPortraitHasMore: boolean;
  libPickerPortraitLoading: boolean;
  libPickerPortraitLoadingMore: boolean;

  /** 上传素材到当前素材组的进行中状态。 */
  libUploading: boolean;
  /** 正在删除的素材 id，用于禁用对应行的删除按钮。 */
  libDeletingAssetId: string | null;

  // --- UI Shared States (Modals / Drawer / Picker) ---
  /** 新建素材组弹窗可见性。 */
  createGroupVisible: boolean;
  /** 新建素材组时的临时名称。 */
  newGroupName: string;
  /** 新建素材组时的临时描述。 */
  newGroupDescription: string;
  /** 待确认删除的素材对象（不为 null 时展示素材删除弹窗）。 */
  libraryAssetAwaitingDelete: Asset | null;
  /** 待确认删除的素材组对象（不为 null 时展示组删除弹窗）。 */
  libraryGroupAwaitingDelete: LibraryGroup | null;
  /** 正在删除组的加载状态。 */
  libraryGroupDeleting: boolean;
  /** 素材选择器目标（nodeId），不为 null 时展示选择器弹窗。 */
  libPickerTarget: string | null;
  /**
   * 非工作台节点选型：与工作台互不干扰；选中后由 onApply 更新对应 UI（如重新生成草稿）。
   */
  libPickerExternalConsumer: LibPickerExternalConsumer | null;
  clearLibPicker: () => void;
  setLibAssetGroups: (libAssetGroups: ValueUpdater<LibraryGroup[]>) => void;
  setLibSelectedGroupId: (libSelectedGroupId: string) => void;
  setLibRemoteAssets: (libRemoteAssets: ValueUpdater<Asset[]>) => void;
  setLibPortraitHasMore: (hasMore: boolean) => void;
  setLibPortraitLoading: (loading: boolean) => void;
  setLibPortraitLoadingMore: (loadingMore: boolean) => void;
  setLibPortraitSearchName: (name: string) => void;
  setLibDrawerSearchName: (name: string) => void;
  setLibPickerSearchName: (name: string) => void;
  setLibDrawerRemoteAssets: (assets: ValueUpdater<Asset[]>) => void;
  setLibDrawerPortraitHasMore: (hasMore: boolean) => void;
  setLibDrawerPortraitLoading: (loading: boolean) => void;
  setLibDrawerPortraitLoadingMore: (loadingMore: boolean) => void;
  setLibPickerRemoteAssets: (assets: ValueUpdater<Asset[]>) => void;
  setLibPickerPortraitHasMore: (hasMore: boolean) => void;
  setLibPickerPortraitLoading: (loading: boolean) => void;
  setLibPickerPortraitLoadingMore: (loadingMore: boolean) => void;
  setLibUploading: (libUploading: boolean) => void;
  setLibDeletingAssetId: (libDeletingAssetId: string | null) => void;
  setCreateGroupVisible: (visible: boolean) => void;
  setNewGroupName: (name: string) => void;
  setNewGroupDescription: (desc: string) => void;
  setLibraryAssetAwaitingDelete: (asset: Asset | null) => void;
  setLibraryGroupAwaitingDelete: (group: LibraryGroup | null) => void;
  setLibraryGroupDeleting: (deleting: boolean) => void;
  setLibPickerTarget: (target: string | null) => void;
  setLibPickerExternalConsumer: (consumer: LibPickerExternalConsumer | null) => void;
}

export const useSeedanceLibraryStore = create<SeedanceLibraryState>((set) => ({
  libAssetGroups: [],
  libSelectedGroupId: getLocalStorageValue(SEEDANCE_STORAGE_KEYS.librarySelectedGroupId),
  libRemoteAssets: [],
  libPortraitHasMore: false,
  libPortraitLoading: false,
  libPortraitLoadingMore: false,
  libPortraitSearchName: '',
  libDrawerSearchName: '',
  libPickerSearchName: '',
  libDrawerRemoteAssets: [],
  libDrawerPortraitHasMore: false,
  libDrawerPortraitLoading: false,
  libDrawerPortraitLoadingMore: false,
  libPickerRemoteAssets: [],
  libPickerPortraitHasMore: false,
  libPickerPortraitLoading: false,
  libPickerPortraitLoadingMore: false,
  libUploading: false,
  libDeletingAssetId: null,
  setLibAssetGroups: (libAssetGroups) =>
    set((state) => ({
      libAssetGroups: resolveValueUpdater(state.libAssetGroups, libAssetGroups),
    })),
  setLibSelectedGroupId: (libSelectedGroupId) => set({ libSelectedGroupId }),
  setLibRemoteAssets: (libRemoteAssets) =>
    set((state) => ({
      libRemoteAssets: resolveValueUpdater(state.libRemoteAssets, libRemoteAssets),
    })),
  setLibPortraitHasMore: (libPortraitHasMore) => set({ libPortraitHasMore }),
  setLibPortraitLoading: (libPortraitLoading) => set({ libPortraitLoading }),
  setLibPortraitLoadingMore: (libPortraitLoadingMore) => set({ libPortraitLoadingMore }),
  setLibPortraitSearchName: (libPortraitSearchName) => set({ libPortraitSearchName }),
  setLibDrawerSearchName: (libDrawerSearchName) => set({ libDrawerSearchName }),
  setLibPickerSearchName: (libPickerSearchName) => set({ libPickerSearchName }),
  setLibDrawerRemoteAssets: (libDrawerRemoteAssets) =>
    set((state) => ({
      libDrawerRemoteAssets: resolveValueUpdater(state.libDrawerRemoteAssets, libDrawerRemoteAssets),
    })),
  setLibDrawerPortraitHasMore: (libDrawerPortraitHasMore) => set({ libDrawerPortraitHasMore }),
  setLibDrawerPortraitLoading: (libDrawerPortraitLoading) => set({ libDrawerPortraitLoading }),
  setLibDrawerPortraitLoadingMore: (libDrawerPortraitLoadingMore) => set({ libDrawerPortraitLoadingMore }),
  setLibPickerRemoteAssets: (libPickerRemoteAssets) =>
    set((state) => ({
      libPickerRemoteAssets: resolveValueUpdater(state.libPickerRemoteAssets, libPickerRemoteAssets),
    })),
  setLibPickerPortraitHasMore: (libPickerPortraitHasMore) => set({ libPickerPortraitHasMore }),
  setLibPickerPortraitLoading: (libPickerPortraitLoading) => set({ libPickerPortraitLoading }),
  setLibPickerPortraitLoadingMore: (libPickerPortraitLoadingMore) => set({ libPickerPortraitLoadingMore }),
  setLibUploading: (libUploading) => set({ libUploading }),
  setLibDeletingAssetId: (libDeletingAssetId) => set({ libDeletingAssetId }),
  
  createGroupVisible: false,
  newGroupName: '',
  newGroupDescription: '',
  libraryAssetAwaitingDelete: null,
  libraryGroupAwaitingDelete: null,
  libraryGroupDeleting: false,
  libPickerTarget: null,
  libPickerExternalConsumer: null,

  setCreateGroupVisible: (createGroupVisible) => set({ createGroupVisible }),
  setNewGroupName: (newGroupName) => set({ newGroupName }),
  setNewGroupDescription: (newGroupDescription) => set({ newGroupDescription }),
  setLibraryAssetAwaitingDelete: (libraryAssetAwaitingDelete) => set({ libraryAssetAwaitingDelete }),
  setLibraryGroupAwaitingDelete: (libraryGroupAwaitingDelete) => set({ libraryGroupAwaitingDelete }),
  setLibraryGroupDeleting: (libraryGroupDeleting) => set({ libraryGroupDeleting }),
  setLibPickerTarget: (libPickerTarget) => set({ libPickerTarget }),
  setLibPickerExternalConsumer: (libPickerExternalConsumer: LibPickerExternalConsumer | null) =>
    set({ libPickerExternalConsumer }),
  clearLibPicker: () => set({ libPickerTarget: null, libPickerExternalConsumer: null }),
}));
